import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Store, ChevronDown, Check } from "lucide-react";

/**
 * VendorPickerChip — the little "🏪 …" affordance next to each grocery
 * row that lets the user pick or change the vendor for that item.
 *
 * Rendering rules:
 *   • Row has a vendor       → shows the vendor's name in a coloured chip.
 *   • Row has no vendor AND
 *     2+ vendors exist       → shows a muted "Pick vendor · N options" chip.
 *   • Otherwise (0 or 1 vendor)→ renders nothing so the row stays clean.
 *
 * Clicking opens a tight dropdown of vendors (grouped: preferred first,
 * then the rest). "Any vendor" clears the assignment.
 */
export default function VendorPickerChip({ item, vendors, preferredVendorId, onChanged }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const options = useMemo(() => {
    if (!vendors) return [];
    // Prefer real shopping vendors — hide gas suppliers (they live on
    // their own page and never sell groceries).
    const filtered = vendors.filter((v) => v.type !== "Gas supplier");
    // Sort: preferred vendor first, then the currently-assigned one,
    // then alphabetical for the rest.
    return [...filtered].sort((a, b) => {
      if (a.id === preferredVendorId) return -1;
      if (b.id === preferredVendorId) return 1;
      if (a.id === item.vendor_id) return -1;
      if (b.id === item.vendor_id) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [vendors, preferredVendorId, item.vendor_id]);

  const currentVendor = options.find((v) => v.id === item.vendor_id) || null;
  const hasEnoughVendors = options.length >= 2;

  const pick = async (vendorId) => {
    if (vendorId === (item.vendor_id || null)) { setOpen(false); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/grocery/${item.id}`, {
        name: item.name,
        quantity: item.quantity || null,
        unit: item.unit || null,
        category: item.category || "Other",
        status: item.status || "upcoming",
        note: item.note || null,
        vendor_id: vendorId,
        purchase_date: item.purchase_date || null,
        expiry_date: item.expiry_date || null,
      });
      void data;
      onChanged && onChanged();
      const name = vendors.find((v) => v.id === vendorId)?.name;
      toast.success(vendorId ? `Set vendor to ${name}` : "Vendor cleared");
    } catch (e) {
      toast.error("Couldn't update the vendor just now.");
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  // Nothing to show if there's no vendor and there's < 2 vendors to pick.
  if (!currentVendor && !hasEnoughVendors) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        data-testid={`grocery-vendor-chip-${item.id}`}
        disabled={saving}
        title={currentVendor ? `Get from ${currentVendor.name} — tap to change` : `${options.length} places nearby — tap to pick`}
        className={
          currentVendor
            ? "inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 text-hearth-clayDark bg-hearth-clay/10 hover:bg-hearth-clay/15 transition"
            : "inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 text-hearth-mute hover:text-hearth-clayDark bg-hearth-stone/60 hover:bg-hearth-clay/10 transition"
        }
      >
        <Store size={11} />
        <span className="truncate max-w-[140px]">
          {currentVendor
            ? currentVendor.name
            : `${options.length} places nearby`}
        </span>
        <ChevronDown size={11} className="opacity-70" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-20 min-w-[220px] rounded-2xl border border-hearth-line bg-hearth-paper shadow-lg py-1.5 max-h-72 overflow-y-auto"
          data-testid={`grocery-vendor-menu-${item.id}`}
        >
          <div className="px-3 py-1 caption text-hearth-mute">
            Get {item.name} from…
          </div>
          <button
            data-testid={`grocery-vendor-any-${item.id}`}
            onClick={() => pick(null)}
            className="w-full text-left px-3 py-1.5 text-sm text-hearth-mute hover:bg-hearth-stone transition flex items-center justify-between"
          >
            <span>Any vendor</span>
            {!item.vendor_id && <Check size={12} className="text-hearth-sage" />}
          </button>
          {options.map((v) => (
            <button
              key={v.id}
              onClick={() => pick(v.id)}
              data-testid={`grocery-vendor-opt-${item.id}-${v.id}`}
              className="w-full text-left px-3 py-1.5 text-sm text-hearth-ink hover:bg-hearth-stone transition flex items-center justify-between gap-2"
            >
              <span className="flex-1 min-w-0 truncate">
                {v.name}
                {v.type && <span className="text-hearth-mute text-[10px] ml-1">· {v.type}</span>}
              </span>
              <span className="flex items-center gap-1">
                {v.id === preferredVendorId && (
                  <span className="text-[9px] uppercase tracking-wider text-hearth-clayDark bg-hearth-clay/10 rounded-full px-1.5 py-[1px]">
                    preferred
                  </span>
                )}
                {v.id === item.vendor_id && <Check size={12} className="text-hearth-sage" />}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
